import React, { Suspense, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import ErrorBoundary from '@/components/ErrorBoundary';
import { ContentSkeleton } from '@/components/Skeleton';
import Hero from '@/components/Hero';
import WhatsAppPromo from '@/components/WhatsAppPromo';
import { ArrowRight, Code2, Terminal, Workflow } from 'lucide-react';

const Index = () => {
  // Log when the component mounts to verify refresh
  useEffect(() => {
    console.log("Index component mounted at:", new Date().toISOString());
  }, []);

  // FAQ Schema Markup
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is DeepSeek AI?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "DeepSeek AI is an AI research company that develops cost-efficient, high-performance language models, providing an open-source alternative to GPT-4."
        }
      },
      {
        "@type": "Question",
        "name": "How does DeepSeek AI compare to GPT-4?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "DeepSeek AI offers high-performance AI models with lower development costs than GPT-4, making it a strong alternative for businesses and developers."
        }
      },
      {
        "@type": "Question",
        "name": "Is DeepSeek AI open-source?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, DeepSeek AI provides open-source models, allowing developers to explore and implement advanced AI capabilities without high licensing fees."
        }
      },
      {
        "@type": "Question",
        "name": "What makes DeepSeek AI different?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "DeepSeek AI stands out with its focus on efficiency, performance, and accessibility. The models are designed to be cost-effective while delivering state-of-the-art results for a variety of AI applications."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>DeepSeek AI</title>
        <meta name="author" content="DeepSeek AI" />
        <meta property="og:title" content="DeepSeek AI" />
        <meta property="og:description" content="DeepSeek AI delivers cutting-edge artificial intelligence solutions." />
        <meta property="og:url" content="https://www.deepseek.ai/" />
        <meta property="og:image" content="/og-image.png" />
        <meta property="og:type" content="website" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DeepSeek AI" />
        <meta name="twitter:description" content="DeepSeek AI delivers cutting-edge artificial intelligence solutions." />
        <meta name="twitter:image" content="/og-image.png" />
        <meta name="google" content="notranslate" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://deepseek.ai/" />
        
        {/* FAQ Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <main className="min-h-screen bg-gray-50">
        <ErrorBoundary>
          <Hero />
        </ErrorBoundary>

        <WhatsAppPromo autoShow={true} />

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" aria-labelledby="solutions-heading">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 id="solutions-heading" className="text-4xl font-bold text-gray-900 mb-4">
                DeepSeek AI Solutions
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Experience our comprehensive suite of AI-powered tools designed to transform your development workflow
              </p>
            </div>
            
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              <article className="group relative p-8 bg-white rounded-2xl shadow-sm transition-all duration-300 hover:shadow-lg border border-gray-100 hover:border-deepseek-primary/20">
                <div className="flex flex-col h-full">
                  <div className="mb-4">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-deepseek-primary/10 text-deepseek-primary">
                      <Terminal className="w-6 h-6" />
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-deepseek-primary transition-colors">
                    DeepSeek AI Development Suite
                  </h3>
                  <p className="text-gray-600 mb-6 flex-grow">
                    Advanced AI-powered development environment for maximum productivity
                  </p>
                  <div className="flex items-center text-deepseek-primary font-medium">
                    Learn more <ArrowRight className="ml-2 w-4 h-4" />
                  </div>
                </div>
              </article>

              <article className="group relative p-8 bg-white rounded-2xl shadow-sm transition-all duration-300 hover:shadow-lg border border-gray-100 hover:border-deepseek-primary/20">
                <div className="flex flex-col h-full">
                  <div className="mb-4">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-deepseek-primary/10 text-deepseek-primary">
                      <Code2 className="w-6 h-6" />
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-deepseek-primary transition-colors">
                    DeepSeek AI Code Analysis
                  </h3>
                  <p className="text-gray-600 mb-6 flex-grow">
                    Intelligent code review and optimization powered by DeepSeek AI
                  </p>
                  <div className="flex items-center text-deepseek-primary font-medium">
                    Learn more <ArrowRight className="ml-2 w-4 h-4" />
                  </div>
                </div>
              </article>

              <article className="group relative p-8 bg-white rounded-2xl shadow-sm transition-all duration-300 hover:shadow-lg border border-gray-100 hover:border-deepseek-primary/20">
                <div className="flex flex-col h-full">
                  <div className="mb-4">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-deepseek-primary/10 text-deepseek-primary">
                      <Workflow className="w-6 h-6" />
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-deepseek-primary transition-colors">
                    DeepSeek AI Workspace
                  </h3>
                  <p className="text-gray-600 mb-6 flex-grow">
                    AI-enhanced development environment powered by DeepSeek AI
                  </p>
                  <div className="flex items-center text-deepseek-primary font-medium">
                    Learn more <ArrowRight className="ml-2 w-4 h-4" />
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Index;
